import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Nav, Tab, Row, Col, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import DocumentsGroupedView from './DocumentsGroupedView';
import DocumentsCardGrid from './DocumentsCardGrid';
import DocumentsTableView from './DocumentsTableView';
import { FaArrowLeft, FaLayerGroup, FaTh, FaTable } from 'react-icons/fa';

const DocumentsShowcase = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [project, setProject] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeView, setActiveView] = useState('grouped');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch project details
        const projectResponse = await fetch(`/api/projects/${id}`, {
          headers: { 'x-auth-token': token }
        });
        
        if (!projectResponse.ok) {
          throw new Error('Failed to fetch project');
        }
        
        const projectData = await projectResponse.json();
        setProject(projectData);
        setDocuments(projectData.documents || []);
        
        // Fetch receipts for this project (optional - feature may not be enabled)
        try {
          const receiptsResponse = await fetch(`/api/receipts/project/${id}`, {
            headers: { 'x-auth-token': token }
          });
          
          if (receiptsResponse.ok) {
            const receiptsData = await receiptsResponse.json();
            setReceipts(receiptsData || []);
          } else {
            // Receipts feature may not be enabled or no receipts yet
            setReceipts([]);
          }
        } catch (err) {
          // Receipts feature may not be available
          console.log('Receipts not available:', err.message);
          setReceipts([]);
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error('Failed to load project data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [id, token]);

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete document');
      }
      
      setDocuments(documents.filter(doc => doc.DocumentId !== documentId));
      toast.success('Document deleted successfully');
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Failed to delete document');
    }
  };

  if (isLoading) {
    return <div className="text-center mt-5">Loading...</div>;
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <Button 
            variant="outline-secondary" 
            className="me-3"
            onClick={() => navigate(`/projects/${id}`)}
          >
            <FaArrowLeft className="me-1" /> Back to Project
          </Button>
          <h2 className="mb-0">Documents View Options</h2>
        </div>
        <Badge bg="info" className="fs-6">
          {project?.ProjectName}
        </Badge>
      </div>

      <Card className="mb-4">
        <Card.Header className="bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <strong>Choose a Layout Style</strong>
            <small className="text-muted">Click each tab to preview the design</small>
          </div>
        </Card.Header>
        <Card.Body>
          <Tab.Container activeKey={activeView} onSelect={setActiveView}>
            <Nav variant="pills" className="mb-4 justify-content-center">
              <Nav.Item>
                <Nav.Link eventKey="grouped" className="mx-2">
                  <FaLayerGroup className="me-2" />
                  Option 1: Grouped Sections
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="cards" className="mx-2">
                  <FaTh className="me-2" />
                  Option 2: Card Grid
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="table" className="mx-2">
                  <FaTable className="me-2" />
                  Option 3: Table with Filters
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              <Tab.Pane eventKey="grouped">
                <div className="border rounded p-3 bg-light mb-3">
                  <h6 className="mb-2">📁 Grouped/Collapsible Sections</h6>
                  <p className="mb-0 text-muted small">
                    Documents organized by type (Proposals, Invoices, Change Orders, Receipts, Other). 
                    Each section can be expanded/collapsed. Great for projects with many documents.
                  </p>
                </div>
                <DocumentsGroupedView 
                  documents={documents} 
                  receipts={receipts}
                  token={token}
                  onDelete={handleDeleteDocument}
                  clientEmail={project?.ClientEmail}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="cards">
                <div className="border rounded p-3 bg-light mb-3">
                  <h6 className="mb-2">🎴 Card Grid Layout</h6>
                  <p className="mb-0 text-muted small">
                    Visual card-based layout with document previews/icons. 
                    Filter tabs at the top. Modern look, works well on mobile.
                  </p>
                </div>
                <DocumentsCardGrid 
                  documents={documents} 
                  receipts={receipts}
                  token={token}
                  onDelete={handleDeleteDocument}
                  clientEmail={project?.ClientEmail}
                />
              </Tab.Pane>

              <Tab.Pane eventKey="table">
                <div className="border rounded p-3 bg-light mb-3">
                  <h6 className="mb-2">📊 Table with Filters</h6>
                  <p className="mb-0 text-muted small">
                    Traditional table view with sortable columns and filter dropdown. 
                    Compact, shows more documents at once. Good for power users.
                  </p>
                </div>
                <DocumentsTableView 
                  documents={documents} 
                  receipts={receipts}
                  token={token}
                  onDelete={handleDeleteDocument}
                  clientEmail={project?.ClientEmail}
                />
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body className="text-center">
          <h5>Which layout do you prefer?</h5>
          <p className="text-muted">Let me know and I'll implement it as the default Documents tab!</p>
        </Card.Body>
      </Card>
    </div>
  );
};

export default DocumentsShowcase;
